/*
 * Adresses belges : suggestions pendant la saisie (Photon, données OpenStreetMap) et contrôle du format
 * « Rue de la Loi 16, 1000 Bruxelles ».
 * Photon est fait pour la saisie semi-automatique ; Nominatim, l'autre service OpenStreetMap, l'interdit.
 */

export interface AddressParts {
  street: string;
  number: string;
  postcode: string;
  city: string;
}

export interface AddressSuggestion {
  key: string;
  label: string;
  parts: AddressParts;
  /** Numéro de maison connu : l'adresse est complète. */
  complete: boolean;
}

interface PhotonProperties {
  countrycode?: string;
  type?: string;
  osm_key?: string;
  name?: string;
  street?: string;
  housenumber?: string;
  postcode?: string;
  city?: string;
  locality?: string;
  district?: string;
}

interface PhotonResponse {
  features?: { properties?: PhotonProperties }[];
}

const PHOTON_URL = 'https://photon.komoot.io/api/';
/** Belgique : ouest, sud, est, nord. */
const BELGIUM_BBOX = '2.54,49.49,6.41,51.51';

export type AddressLang = 'fr' | 'nl' | 'en';

/** Bruxelles : noms bilingues « Rue de la Loi - Wetstraat », « Ixelles - Elsene » → partie française (néerlandaise en nl). */
function localPart(value: string, postcode: string, lang: AddressLang): string {
  const pc = Number(postcode);
  const v = value.trim();
  if (!(pc >= 1000 && pc <= 1299 && v.includes(' - '))) return v;
  const [fr, nl] = v.split(' - ').map(x => x.trim());
  return lang === 'nl' && nl ? nl : fr;
}

export function formatAddress(p: AddressParts): string {
  const street = [p.street.trim(), p.number.trim()].filter(Boolean).join(' ');
  const locality = [p.postcode.trim(), p.city.trim()].filter(Boolean).join(' ');
  return [street, locality].filter(Boolean).join(', ');
}

/** Découpe « Rue X 12, 1050 Ixelles » (saisie libre ou adresse déjà enregistrée). */
export function parseAddress(value: string): AddressParts {
  const m = /^(.*?)(?:\s+(\d+[A-Za-z]?(?:\s*(?:bte|boîte|bus|b)\.?\s*\S+)?))?\s*,\s*(\d{4})\s+(.+)$/i.exec(value.trim());
  if (!m) return { street: value.trim(), number: '', postcode: '', city: '' };
  return { street: m[1].trim(), number: (m[2] ?? '').trim(), postcode: m[3], city: m[4].trim() };
}

function suggestionFrom(p: PhotonProperties, lang: AddressLang): AddressSuggestion | null {
  if ((p.countrycode ?? '').toUpperCase() !== 'BE') return null;
  const postcode = (p.postcode ?? '').trim();
  if (!/^\d{4}$/.test(postcode)) return null;
  const streetRaw = p.street ?? (p.type === 'street' || p.osm_key === 'highway' ? p.name : undefined);
  const cityRaw = p.city ?? p.locality ?? p.district;
  if (!streetRaw || !cityRaw) return null;
  const parts: AddressParts = {
    street: localPart(streetRaw, postcode, lang),
    number: (p.housenumber ?? '').trim(),
    postcode,
    city: localPart(cityRaw, postcode, lang),
  };
  const label = formatAddress(parts);
  return { key: label.toLowerCase(), label, parts, complete: !!parts.number };
}

/** Jusqu'à 6 adresses belges pour le texte saisi (adresses complètes d'abord). */
export async function searchAddresses(query: string, signal?: AbortSignal, lang: AddressLang = 'fr'): Promise<AddressSuggestion[]> {
  const base = `${PHOTON_URL}?q=${encodeURIComponent(query)}&limit=12&lat=50.85&lon=4.35&bbox=${BELGIUM_BBOX}`;
  // Photon ne propose pas le néerlandais : noms locaux (bilingues à Bruxelles), puis partie néerlandaise.
  let res = await fetch(`${base}${lang === 'nl' ? '' : '&lang=fr'}&layer=house&layer=street`, { signal });
  // Langue ou filtre de couches refusés par le serveur : recherche simple.
  if (!res.ok) res = await fetch(base, { signal });
  if (!res.ok) throw new Error(`Recherche d’adresse indisponible (${res.status})`);
  const data = (await res.json()) as PhotonResponse;
  const seen = new Set<string>();
  const out: AddressSuggestion[] = [];
  for (const f of data.features ?? []) {
    const s = f.properties ? suggestionFrom(f.properties, lang) : null;
    if (!s || seen.has(s.key)) continue;
    seen.add(s.key);
    out.push(s);
  }
  return out.sort((a, b) => Number(b.complete) - Number(a.complete)).slice(0, 6);
}

export type AddressErrorCode = 'required' | 'addressIncomplete' | 'addressStreet' | 'addressNumber';

/** Adresse complète attendue : rue, numéro, code postal à 4 chiffres et commune. Code d'erreur (clés « join.errors.* »), null si correcte. */
export function addressErrorCode(value: string | null | undefined): AddressErrorCode | null {
  const v = (value ?? '').trim();
  if (!v) return 'required';
  const m = /^(.*\S)\s*,\s*(\d{4})\s+(\S.*)$/.exec(v);
  if (!m) return 'addressIncomplete';
  if (!/[A-Za-zÀ-ÿ]{2}/.test(m[1])) return 'addressStreet';
  if (!/\d/.test(m[1])) return 'addressNumber';
  return null;
}

const ADDRESS_ERRORS_FR: Record<AddressErrorCode, string> = {
  required: 'Champ requis',
  addressIncomplete: 'Adresse incomplète : rue, numéro, code postal et commune (ex. Rue de la Loi 16, 1000 Bruxelles)',
  addressStreet: 'Nom de rue manquant',
  addressNumber: 'Numéro manquant (ex. Rue de la Loi 16, 1000 Bruxelles)',
};

/** Message en français, null si l'adresse est correcte. */
export function addressError(value: string | null | undefined): string | null {
  const code = addressErrorCode(value);
  return code ? ADDRESS_ERRORS_FR[code] : null;
}
